<template>
  <nav class="hidden md:flex mb-4 h-8" aria-label="Breadcrumb">
    <ol role="list" class="flex space-x-4 rounded-md bg-white px-6 shadow">
      <li class="flex">
        <div class="flex items-center">
          <router-link to="/hub" class="transition ease-in-out duration-300 text-gray-400 hover:text-gray-500">
            <HomeIcon class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            <span class="sr-only">Home</span>
          </router-link>
        </div>
      </li>
      <li class="flex">
        <div class="flex items-center">
          <svg class="h-full w-6 flex-shrink-0 text-gray-200" viewBox="0 0 24 44" preserveAspectRatio="none" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
          </svg>
          <router-link to="/dashboard/storage" class="transition ease-in-out duration-300 ml-4 text-xs font-medium text-gray-500 hover:text-gray-700" aria-current="page">Moduł magazyn</router-link>
        </div>
      </li>
      <li class="flex">
        <div class="flex items-center">
          <svg class="h-full w-6 flex-shrink-0 text-gray-200" viewBox="0 0 24 44" preserveAspectRatio="none" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
          </svg>
          <div class="ml-4 text-xs font-medium text-gray-400 cursor-default" aria-current="page">Połącz paczki wariantów</div>
        </div>
      </li>
    </ol>
  </nav>

  <router-link to="/dashboard/storage" class="block md:hidden transition ease-in-out duration-300 mb-2 text-sm font-medium text-blue-500 hover:text-blue-700" aria-current="page">
    &larr; Cofnij
  </router-link>

  <div class="flex min-h-0 flex-col py-2">
    <div class="flex-shrink-0 border-b border-gray-200">
      <nav class="-mb-px flex space-x-8 select-none" aria-label="Tabs">
        <a @click.prevent="openTab(0)" href="#" :class="[activeTab === 'create' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700', 'group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium']">
          <ViewGridAddIcon :class="[activeTab === 'create' ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500', '-ml-0.5 mr-2 h-5 w-5']" aria-hidden="true" />
          <span>Łączenie paczek</span>
        </a>
        <a @click.prevent="openTab(1)" href="#" :class="[activeTab === 'list' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700', 'group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium']">
          <ViewGridIcon :class="[activeTab === 'list' ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500', '-ml-0.5 mr-2 h-5 w-5']" aria-hidden="true" />
          <span>Lista połączeń</span>
        </a>
      </nav>
    </div>

    <section v-if="activeTab === 'create'" class="mx-auto mt-4 flex min-h-0 w-full max-w-7xl flex-col overflow-hidden" style="height: calc(100vh - 310px); max-height: calc(100vh - 310px);">
      <div class="mb-3 flex flex-shrink-0 flex-col gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
        <div class="grid grid-cols-1 gap-2 xl:grid-cols-[1fr_1fr_1fr_320px]">
          <div :class="[!selectedSourcePackage ? 'border-indigo-300 bg-indigo-50 text-indigo-900' : 'border-green-200 bg-green-50 text-green-800', 'rounded-md border px-3 py-2 text-xs']">
            <span class="font-bold">1.</span> Wybierz pierwszą paczkę.
          </div>
          <div :class="[selectedSourcePackage && !selectedTargetPackage ? 'border-indigo-300 bg-indigo-50 text-indigo-900' : selectedTargetPackage ? 'border-green-200 bg-green-50 text-green-800' : 'border-gray-200 bg-gray-50 text-gray-500', 'rounded-md border px-3 py-2 text-xs']">
            <span class="font-bold">2.</span> Wybierz drugą paczkę z innego wariantu.
          </div>
          <div :class="[canCreateConnection ? 'border-indigo-300 bg-indigo-50 text-indigo-900' : 'border-gray-200 bg-gray-50 text-gray-500', 'rounded-md border px-3 py-2 text-xs']">
            <span class="font-bold">3.</span> Zapisz zamienniki.
          </div>
          <div class="grid grid-cols-2 gap-2">
            <button @click="createConnection" :disabled="saving || !canCreateConnection" :class="[saving || !canCreateConnection ? 'opacity-40 pointer-events-none' : '', 'inline-flex min-h-[34px] w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-indigo-700 active:scale-[0.96]']">
              <CheckCircleIcon class="mr-2 h-4 w-4" />
              Zapisz
            </button>
            <button @click="clearSelection" :disabled="!selectedSourcePackage && !selectedTargetPackage" :class="[!selectedSourcePackage && !selectedTargetPackage ? 'opacity-40 pointer-events-none' : '', 'inline-flex min-h-[34px] w-full items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition-colors duration-200 hover:bg-gray-50 active:scale-[0.96]']">
              Wyczyść
            </button>
          </div>
        </div>

        <div class="grid min-w-0 grid-cols-1 gap-2 lg:grid-cols-2 lg:items-stretch">
          <PackageSlot title="Paczka A" :item="selectedSourcePackage" @clear="selectedSourcePackage = null" />
          <PackageSlot title="Paczka B" :item="selectedTargetPackage" @clear="selectedTargetPackage = null" />
        </div>

        <p class="px-1 text-xs text-gray-400">{{ createConnectionHint }}</p>
      </div>

      <div class="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden lg:grid-cols-12">
        <div class="flex min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm lg:col-span-4" style="height: calc(100vh - 455px); max-height: calc(100vh - 455px);">
          <div class="flex-shrink-0 border-b border-gray-200 p-4">
            <label class="block text-xs font-semibold uppercase text-gray-500">Produkty</label>
            <input v-model="productSearch" type="text" placeholder="Szukaj produktu, producenta lub ID" class="mt-2 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
          </div>

          <LoadingSpinnerHub v-if="loadingProducts" class="py-8" />
          <div v-else class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <button v-for="productItem in filteredProducts" :key="productItem.id" @click="selectProduct(productItem)" :class="[selectedProduct?.id === productItem.id ? 'bg-indigo-50 ring-1 ring-inset ring-indigo-200' : 'hover:bg-gray-50', 'w-full border-b border-gray-100 px-4 py-3 text-left transition-all duration-200']">
              <div class="flex items-center gap-3">
                <img class="h-9 w-9 flex-none rounded-full bg-gray-50" :src="`https://api.dicebear.com/7.x/initials/svg?seed=${productItem.name}`" alt="" />
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-semibold text-gray-900">{{ productItem.name }}</p>
                  <p class="truncate text-xs text-gray-500">{{ getManufacturerName(productItem) || 'Brak producenta' }}</p>
                </div>
                <span class="text-[11px] font-semibold text-gray-400">{{ countConnectableVariants(productItem) }}/{{ Object.keys(productItem.variants || {}).length }}</span>
              </div>
            </button>
            <EmptyState v-if="filteredProducts.length === 0" class="m-4" />
          </div>

          <div class="flex-shrink-0 border-t border-gray-200 p-3">
            <button @click="loadMoreProducts" :disabled="queryLimitReached || loadingProducts" :class="[queryLimitReached || loadingProducts ? 'opacity-40 pointer-events-none' : '', 'w-full rounded-md bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 transition-all duration-300 hover:bg-indigo-100']">
              Załaduj więcej
            </button>
          </div>
        </div>

        <div class="flex min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm lg:col-span-8" style="height: calc(100vh - 455px); max-height: calc(100vh - 455px);">
          <div class="flex-shrink-0 border-b border-gray-200 p-4">
            <p class="text-xs font-semibold uppercase text-gray-500">Warianty i paczki</p>
            <p class="mt-1 truncate text-sm font-semibold text-gray-900">{{ selectedProduct ? selectedProduct.name : 'Wybierz produkt z listy' }}</p>
          </div>

          <div v-if="!selectedProduct" class="p-4">
            <EmptyState />
          </div>

          <div v-else class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div v-for="variant in selectedProductVariants" :key="variant.variantId" class="border-b border-gray-100">
              <button @click="toggleVariant(variant.variantId)" :disabled="!hasConnectableParts(variant)" :class="[!hasConnectableParts(variant) ? 'cursor-not-allowed opacity-45' : 'hover:bg-gray-50', 'flex w-full items-center justify-between px-4 py-3 text-left transition-all duration-200']">
                <div class="min-w-0">
                  <p class="truncate text-sm font-semibold text-gray-900">{{ variant.name || variant.variantId }}</p>
                  <p class="truncate text-xs text-gray-500">ID: {{ variant.variantId }}</p>
                </div>
                <div class="flex items-center gap-2">
                  <span v-if="!hasConnectableParts(variant)" class="inline-flex min-w-[74px] items-center justify-end gap-2 text-xs font-semibold text-gray-400">
                    <span class="h-1.5 w-1.5 rounded-full bg-gray-300"></span>
                    brak
                  </span>
                  <span v-else class="inline-flex min-w-[74px] items-center justify-end gap-2 text-xs font-semibold text-green-700">
                    <span class="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                    {{ getVariantParts(variant).length }} pacz.
                  </span>
                  <ChevronDownIcon :class="[expandedVariants[variant.variantId] ? 'rotate-180' : '', 'h-5 w-5 text-gray-400 transition-all duration-200']" />
                </div>
              </button>

              <div v-if="expandedVariants[variant.variantId] && hasConnectableParts(variant)" class="bg-gray-50 px-4 pb-3">
                <div class="grid grid-cols-1 gap-2 pt-2 sm:grid-cols-2">
                  <div v-for="part in getVariantParts(variant)" :key="part.partKey" :class="[getPackageRole(selectedProduct.id, variant.variantId, part.partKey) ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white', 'rounded-md border p-3 text-sm transition-all duration-200']">
                    <div class="flex items-start justify-between gap-2">
                      <div class="min-w-0">
                        <p class="font-semibold text-gray-900">Paczka {{ part.partKey }}</p>
                        <p class="mt-1 text-[11px] text-gray-400">Stan: {{ normalizeQuantity(part.quantity) }}</p>
                      </div>
                      <span v-if="getPackageRole(selectedProduct.id, variant.variantId, part.partKey)" class="rounded-full bg-indigo-100 px-2 py-1 text-[10px] font-bold uppercase text-indigo-700">
                        {{ getPackageRole(selectedProduct.id, variant.variantId, part.partKey) === 'source' ? 'Paczka A' : 'Paczka B' }}
                      </span>
                    </div>
                    <button @click="selectPackage(selectedProduct, variant, part)" :disabled="!canSelectPackage(selectedProduct, variant, part)" :class="[getPackageRole(selectedProduct.id, variant.variantId, part.partKey) ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-700 hover:bg-indigo-50', !canSelectPackage(selectedProduct, variant, part) ? 'opacity-40 pointer-events-none' : '', 'mt-3 inline-flex min-h-[40px] w-full items-center justify-center rounded-md border border-indigo-200 px-3 py-2 text-xs font-semibold transition-colors duration-200 active:scale-[0.96]']">
                      {{ getPackageSelectLabel(selectedProduct.id, variant.variantId, part.partKey) }}
                    </button>
                    <p v-if="getPackageDisabledReason(selectedProduct, variant, part)" class="mt-2 text-[11px] font-medium text-red-500">
                      {{ getPackageDisabledReason(selectedProduct, variant, part) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>

    <section v-if="activeTab === 'list'" class="mx-auto mt-4 flex h-[calc(100vh-185px)] min-h-0 w-full max-w-7xl flex-col overflow-hidden">
      <div class="mb-3 flex flex-shrink-0 items-end justify-between gap-4">
        <div>
          <h1 class="text-xl font-bold text-gray-900">Lista połączeń paczek</h1>
          <p class="mt-1 text-sm text-gray-500">Aktywne pary zamienników używane później przy ściąganiu paczek ze stanu.</p>
        </div>
        <button @click="getConnectors" class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all duration-300 hover:bg-gray-50">
          Odśwież
        </button>
      </div>
      <div class="mb-3 flex-shrink-0">
        <input v-model="connectionSearch" type="text" placeholder="Szukaj po produkcie, wariancie, paczce lub ID połączenia" class="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
      </div>

      <LoadingSpinnerHub v-if="loadingConnectors" />
      <div v-else class="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table v-if="filteredConnectors.length > 0" class="min-w-full divide-y divide-gray-200">
          <thead class="sticky top-0 z-10 bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Połączenie</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Paczka A</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Paczka B</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">Akcje</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 bg-white">
            <tr v-for="connector in filteredConnectors" :key="connector.id">
              <td class="px-4 py-4 align-top text-sm text-gray-900">
                <p class="font-semibold">{{ connector.id }}</p>
                <p class="mt-1 text-xs text-gray-500">{{ formatConnectorDate(connector) }}</p>
              </td>
              <td class="px-4 py-4 align-top text-sm text-gray-700">
                <ConnectionItem :item="getConnectionSource(connector)" />
              </td>
              <td class="px-4 py-4 align-top text-sm text-gray-700">
                <ConnectionItem :item="getConnectionTarget(connector)" />
              </td>
              <td class="px-4 py-4 text-right align-top">
                <button @click="deleteConnection(connector)" class="text-sm font-semibold text-red-600 transition-all duration-200 hover:text-red-800">Usuń</button>
              </td>
            </tr>
          </tbody>
        </table>
        <EmptyState v-if="filteredConnectors.length === 0" class="m-4" />
      </div>
    </section>
  </div>
</template>

<script>
import { db } from "@/firebase/gfbconf.js";
import moment from "moment";
import LoadingSpinnerHub from "../components/LoadingSpinnerHub.vue";
import EmptyState from "../components/EmptyState.vue";
import { HomeIcon, CheckCircleIcon, ChevronDownIcon, XIcon } from "@heroicons/vue/solid";
import { ViewGridAddIcon, ViewGridIcon } from "@heroicons/vue/outline";

const PackageSlot = {
  props: ["title", "item"],
  emits: ["clear"],
  components: { XIcon },
  template: `
    <div :class="[item ? 'border-indigo-200 bg-indigo-50' : 'border-dashed border-gray-300 bg-gray-50', 'flex min-h-[58px] items-center gap-3 rounded-md border px-3 py-2']">
      <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-white text-xs font-bold uppercase text-gray-500 shadow-sm">
        {{ title.replace('Paczka ', '') }}
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-[11px] font-bold uppercase tracking-wide text-gray-500">{{ title }}</p>
        <p v-if="!item" class="mt-0.5 truncate text-sm text-gray-400">Nie wybrano paczki</p>
        <template v-else>
          <p class="mt-0.5 truncate text-sm font-semibold text-gray-900">{{ item.productName }}</p>
          <p class="truncate text-xs text-gray-600">{{ item.variantName }} · Paczka {{ item.partKey }}{{ item.partName ? ': ' + item.partName : '' }}</p>
        </template>
      </div>
      <button v-if="item" @click="$emit('clear')" class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors duration-200 hover:bg-white hover:text-red-500 active:scale-[0.96]">
        <XIcon class="h-4 w-4" />
      </button>
    </div>
  `,
};

const ConnectionItem = {
  props: ["item"],
  template: `
    <div v-if="item" class="min-w-0">
      <p class="truncate font-semibold text-gray-900">{{ item.productName }}</p>
      <p class="truncate text-xs text-gray-500">{{ item.variantName }}</p>
      <p class="mt-1 text-xs font-semibold text-indigo-700">Paczka {{ item.partKey }}: {{ item.partName || 'Bez nazwy' }}</p>
    </div>
    <span v-else class="text-xs text-gray-400">Brak danych</span>
  `,
};

export default {
  components: {
    ConnectionItem,
    EmptyState,
    LoadingSpinnerHub,
    PackageSlot,
    HomeIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ViewGridAddIcon,
    ViewGridIcon,
  },
  data() {
    return {
      activeTab: "create",
      loadingProducts: false,
      loadingConnectors: false,
      saving: false,
      productSearch: "",
      connectionSearch: "",
      products: [],
      connectors: [],
      selectedProduct: null,
      selectedSourcePackage: null,
      selectedTargetPackage: null,
      expandedVariants: {},
      queryLimit: 50,
      queryLimitReached: false,
      productsListener: null,
    };
  },
  computed: {
    filteredProducts() {
      const query = this.productSearch.trim().toLowerCase();
      if(!query) return this.products;

      return this.products.filter((product) => {
        const manufacturer = this.getManufacturerName(product);
        return [product.id, product.name, manufacturer]
          .filter(Boolean)
          .some((value) => value.toString().toLowerCase().includes(query));
      });
    },
    selectedProductVariants() {
      if(!this.selectedProduct?.variants) return [];
      return Object.entries(this.selectedProduct.variants).map(([variantId, variant]) => ({
        ...(variant || {}),
        variantId,
      }));
    },
    selectedPairPackages() {
      return [this.selectedSourcePackage, this.selectedTargetPackage].filter(Boolean);
    },
    canCreateConnection() {
      if(!this.selectedSourcePackage || !this.selectedTargetPackage) return false;
      if(this.selectedSourcePackage.packageKey === this.selectedTargetPackage.packageKey) return false;
      return this.getVariantPairKey(this.selectedSourcePackage) !== this.getVariantPairKey(this.selectedTargetPackage);
    },
    createConnectionHint() {
      if(!this.selectedSourcePackage && !this.selectedTargetPackage) return "Wybierz pierwszą paczkę.";
      if(this.selectedSourcePackage && !this.selectedTargetPackage) return "Teraz wybierz drugą paczkę z innego wariantu.";
      if(!this.selectedSourcePackage || !this.selectedTargetPackage) return "Wybierz dwie paczki do połączenia.";
      if(this.selectedSourcePackage.packageKey === this.selectedTargetPackage.packageKey) return "Nie można połączyć paczki z samą sobą.";
      if(this.getVariantPairKey(this.selectedSourcePackage) === this.getVariantPairKey(this.selectedTargetPackage)) return "Paczki muszą pochodzić z dwóch różnych wariantów.";
      return "Zapis oznacza, że te paczki są dla siebie zamiennikami.";
    },
    filteredConnectors() {
      const query = this.connectionSearch.trim().toLowerCase();
      if(!query) return this.connectors;

      return this.connectors.filter((connector) => {
        const source = this.getConnectionSource(connector);
        const target = this.getConnectionTarget(connector);
        return [
          connector.id,
          source?.productName,
          source?.variantName,
          source?.partName,
          source?.partKey,
          target?.productName,
          target?.variantName,
          target?.partName,
          target?.partKey,
        ].filter(Boolean).some((item) => item.toString().toLowerCase().includes(query));
      });
    },
  },
  created() {
    moment.locale("pl");
    this.getAllProducts();
  },
  beforeUnmount() {
    if(this.productsListener) this.productsListener();
  },
  methods: {
    openTab(index) {
      this.activeTab = index === 0 ? "create" : "list";
      if(this.activeTab === "create") this.getAllProducts();
      if(this.activeTab === "list") this.getConnectors();
    },
    getManufacturerName(product) {
      if(!product) return "";
      return typeof product.manufacturer === "object" ? product.manufacturer?.name : product.manufacturer;
    },
    normalizeQuantity(value) {
      const quantity = Number(value || 0);
      return Number.isNaN(quantity) ? 0 : quantity;
    },
    makePackageKey(productId, variantId, partKey) {
      return `${productId}__${variantId}__${partKey}`;
    },
    getVariantPairKey(item) {
      return `${item?.productId || ""}__${item?.variantId || ""}`;
    },
    hasConnectableParts(variant) {
      return this.getVariantParts(variant).length > 0;
    },
    getVariantParts(variant) {
      if(!variant?.parts || typeof variant.parts !== "object") return [];

      return Object.entries(variant.parts)
        .filter(([partKey, part]) => {
          if(partKey === "null" || partKey === "undefined" || partKey === "") return false;
          return part && typeof part === "object";
        })
        .map(([partKey, part]) => ({
          ...(part || {}),
          partKey,
        }));
    },
    countConnectableVariants(product) {
      return Object.values(product?.variants || {}).filter((variant) => this.hasConnectableParts(variant)).length;
    },
    selectProduct(product) {
      this.selectedProduct = product;
      this.expandedVariants = {};
    },
    toggleVariant(variantId) {
      this.expandedVariants[variantId] = !this.expandedVariants[variantId];
    },
    createPackagePayload(product, variant, part) {
      return {
        packageKey: this.makePackageKey(product.id, variant.variantId, part.partKey),
        productId: product.id,
        productName: product.name || product.id,
        manufacturerName: this.getManufacturerName(product) || "",
        variantId: variant.variantId,
        variantName: variant.name || variant.variantId,
        partKey: part.partKey,
        partName: part.name || "",
        quantity: this.normalizeQuantity(part.quantity),
      };
    },
    canSetPackageSlot(slot, product, variant, part) {
      const packageKey = this.makePackageKey(product.id, variant.variantId, part.partKey);
      const variantKey = `${product.id}__${variant.variantId}`;

      if(slot === "source") {
        if(this.selectedTargetPackage?.packageKey === packageKey) return false;
        if(this.selectedTargetPackage && this.getVariantPairKey(this.selectedTargetPackage) === variantKey) return false;
        return true;
      }

      if(!this.selectedSourcePackage) return false;
      if(this.selectedSourcePackage?.packageKey === packageKey) return false;
      if(this.getVariantPairKey(this.selectedSourcePackage) === variantKey) return false;
      return true;
    },
    canSelectPackage(product, variant, part) {
      const packageKey = this.makePackageKey(product.id, variant.variantId, part.partKey);
      const role = this.getPackageRole(product.id, variant.variantId, part.partKey);
      if(role) return true;
      if(!this.selectedSourcePackage) return true;
      if(this.selectedSourcePackage?.packageKey === packageKey) return false;
      if(this.getVariantPairKey(this.selectedSourcePackage) === `${product.id}__${variant.variantId}`) return false;
      return true;
    },
    getPackageDisabledReason(product, variant, part) {
      const packageKey = this.makePackageKey(product.id, variant.variantId, part.partKey);
      const variantKey = `${product.id}__${variant.variantId}`;

      if(this.selectedSourcePackage?.packageKey === packageKey) return "";
      if(this.selectedTargetPackage?.packageKey === packageKey) return "";
      if(this.selectedSourcePackage && this.getVariantPairKey(this.selectedSourcePackage) === variantKey) return "Druga paczka musi być z innego wariantu.";
      return "";
    },
    getPackageSelectLabel(productId, variantId, partKey) {
      const role = this.getPackageRole(productId, variantId, partKey);
      if(role === "source") return "Wybrano jako A";
      if(role === "target") return "Wybrano jako B";
      if(!this.selectedSourcePackage) return "Wybierz";
      return this.selectedTargetPackage ? "Zmień paczkę B" : "Wybierz";
    },
    selectPackage(product, variant, part) {
      const payload = this.createPackagePayload(product, variant, part);
      const role = this.getPackageRole(product.id, variant.variantId, part.partKey);

      if(role === "source") {
        this.selectedSourcePackage = this.selectedTargetPackage;
        this.selectedTargetPackage = null;
        return;
      }

      if(role === "target") {
        this.selectedTargetPackage = null;
        return;
      }

      if(!this.selectedSourcePackage) {
        this.selectedSourcePackage = payload;
        return;
      }

      if(!this.canSetPackageSlot("target", product, variant, part)) return;
      this.selectedTargetPackage = payload;
    },
    setPackageSlot(slot, product, variant, part) {
      const payload = this.createPackagePayload(product, variant, part);
      if(!this.canSetPackageSlot(slot, product, variant, part)) return;

      if(slot === "source") {
        this.selectedSourcePackage = payload;
      } else {
        this.selectedTargetPackage = payload;
      }
    },
    getPackageRole(productId, variantId, partKey) {
      const packageKey = this.makePackageKey(productId, variantId, partKey);
      if(this.selectedSourcePackage?.packageKey === packageKey) return "source";
      if(this.selectedTargetPackage?.packageKey === packageKey) return "target";
      return null;
    },
    clearSelection() {
      this.selectedSourcePackage = null;
      this.selectedTargetPackage = null;
      this.expandedVariants = {};
    },
    notify({ head, subheader, success }) {
      this.$store.commit("setNotification", {
        show: true,
        head,
        subheader,
        success,
      });
    },
    async findExistingConnections(packageKeys) {
      const uniqueKeys = [...new Set(packageKeys)];
      const chunks = [];
      for(let i = 0; i < uniqueKeys.length; i += 10) {
        chunks.push(uniqueKeys.slice(i, i + 10));
      }

      const found = [];
      for(const chunk of chunks) {
        const snapshot = await db.collection("ConnectedProductVariants")
          .where("packageKeys", "array-contains-any", chunk)
          .get();

        snapshot.forEach((doc) => {
          const data = doc.data();
          if(data.active === false) return;
          found.push(data);
        });
      }

      return found;
    },
    async createConnection() {
      if(!this.canCreateConnection) return;

      this.saving = true;
      try {
        const items = [this.selectedSourcePackage, this.selectedTargetPackage];
        const packageKeys = items.map((item) => item.packageKey);
        const existingConnections = await this.findExistingConnections(packageKeys);
        if(existingConnections.length > 0) {
          const existing = existingConnections[0];
          const usedPackage = items.find((item) => existing.packageKeys?.includes(item.packageKey));
          const connectedWith = (existing.items || [])
            .filter((item) => item.packageKey !== usedPackage?.packageKey)
            .map((item) => `${item.productName || item.productId} / ${item.variantName || item.variantId} / Paczka ${item.partKey}`)
            .join(", ");
          throw new Error(`${usedPackage ? `Paczka ${usedPackage.partKey} (${usedPackage.productName})` : "Jedna z wybranych paczek"} jest już połączona${connectedWith ? ` z: ${connectedWith}` : "."}`);
        }

        const connectionRef = db.collection("ConnectedProductVariants").doc();
        const now = moment();
        const payload = {
          id: connectionRef.id,
          type: "package-alternative-pair",
          active: true,
          bidirectional: true,
          direction: "mutual-alternative",
          productIds: [...new Set(items.map((item) => item.productId))],
          variantIds: [...new Set(items.map((item) => item.variantId))],
          packageKeys,
          source: { ...this.selectedSourcePackage },
          target: { ...this.selectedTargetPackage },
          items: items.map((item) => ({ ...item })),
          meta: {
            created: {
              asText: now.format("YYYY-MM-DD"),
              unix: now.unix(),
              date: now.toDate(),
              dayOfWeek: now.day(),
              dayNum: now.date(),
              monthNum: now.month() + 1,
              year: now.year(),
              weekNum: now.week(),
            },
            createdBy: this.$store.state.userData ? `${this.$store.state.userData.name} ${this.$store.state.userData.surname}` : "System",
            createdById: this.$store.state.userData?.id || "",
          },
        };

        await connectionRef.set(payload);
        this.notify({
          head: "Utworzono połączenie paczek.",
          subheader: "Paczki zostały zapisane jako zamienniki.",
          success: true,
        });
        this.clearSelection();
      } catch (error) {
        console.log(error);
        this.notify({
          head: "Nie udało się utworzyć połączenia.",
          subheader: error?.message || "Sprawdź wybrane paczki i spróbuj ponownie.",
          success: false,
        });
      } finally {
        this.saving = false;
      }
    },
    async deleteConnection(connector) {
      if(!connector?.id) return;
      const accepted = window.confirm("Czy na pewno usunąć to połączenie paczek?");
      if(!accepted) return;

      this.loadingConnectors = true;
      try {
        await db.collection("ConnectedProductVariants").doc(connector.id).update({
          active: false,
          "meta.deleted": {
            unix: moment().unix(),
            date: moment().toDate(),
            by: this.$store.state.userData ? `${this.$store.state.userData.name} ${this.$store.state.userData.surname}` : "System",
            byId: this.$store.state.userData?.id || "",
          },
        });
        this.notify({
          head: "Usunięto połączenie paczek.",
          subheader: "Połączenie zostało dezaktywowane.",
          success: true,
        });
        await this.getConnectors();
      } catch (error) {
        console.log(error);
        this.notify({
          head: "Nie udało się usunąć połączenia.",
          subheader: "Spróbuj ponownie za chwilę.",
          success: false,
        });
      } finally {
        this.loadingConnectors = false;
      }
    },
    async getConnectors() {
      this.loadingConnectors = true;
      try {
        const snapshot = await db.collection("ConnectedProductVariants")
          .where("active", "==", true)
          .limit(100)
          .get();

        this.connectors = snapshot.docs
          .map((doc) => doc.data())
          .filter((connector) => Array.isArray(connector.items));
      } catch (error) {
        console.log(error);
        this.notify({
          head: "Nie udało się pobrać połączeń.",
          subheader: "Lista połączeń nie została załadowana.",
          success: false,
        });
      } finally {
        this.loadingConnectors = false;
      }
    },
    getConnectionSource(connector) {
      return connector?.source || connector?.items?.[0] || null;
    },
    getConnectionTarget(connector) {
      return connector?.target || connector?.items?.[1] || null;
    },
    formatConnectorDate(connector) {
      const unix = connector?.meta?.created?.unix;
      return unix ? moment.unix(unix).format("DD.MM.YYYY HH:mm") : "Brak daty";
    },
    loadMoreProducts() {
      this.queryLimit += 50;
      this.getAllProducts();
    },
    getAllProducts() {
      this.loadingProducts = true;
      if(this.productsListener) {
        this.productsListener();
        this.productsListener = null;
      }

      this.productsListener = db.collection("Products")
        .limit(this.queryLimit)
        .onSnapshot((snapshot) => {
          this.queryLimitReached = snapshot.docs.length < this.queryLimit;
          this.products = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));

          if(this.selectedProduct) {
            const refreshedProduct = this.products.find((product) => product.id === this.selectedProduct.id);
            if(refreshedProduct) this.selectedProduct = refreshedProduct;
          }

          this.loadingProducts = false;
        }, (error) => {
          console.log(error);
          this.loadingProducts = false;
          this.notify({
            head: "Nie udało się pobrać produktów.",
            subheader: "Odśwież stronę i spróbuj ponownie.",
            success: false,
          });
        });
    },
  },
};
</script>
